import React, { useState } from 'react'
import {
	Box, Table, TableBody,
	TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import {
	AppAntSwitch, AppButtonIcon, AppCardTitle, AppNote, AppTableSkeleton, AppToolbarCreateButton, AppValueChip, AppInfoButton,
	AppEmptyLine,
} from '@components'
import { dsColorNew } from '@palette'
import SweepPreview from './SweepPreview'
import Drawer from './Drawer'
import { formatPreview } from '../@services/previewFormat'
import { WEEKDAY_LABELS, ruleHoursForDay } from '../mockData'
import { useScheduleRules, useSweepPreview } from '../@services/useSchedule'
import useConfirm from 'Pages/DsRepricing/@components/useConfirm'
import useToast from '@hooks/useToast'
import { tonePillSx } from 'Pages/DsRepricing/@components/tones'

/**
 * Schedule Rules — CRUD table.
 *
 * Rules define WHEN the engine runs. Overlap on same (offices, acriss)
 * scope is rejected at save time; different scopes are allowed on the
 * same DBA range so a fleet can be sliced by airport / city / segment.
 */
export default function Rules() {
	const { rules, loading, save, remove, setActive, runNow, describeError } = useScheduleRules()
	const preview = useSweepPreview()
	const [confirm, confirmDialog] = useConfirm()
	const { showToast } = useToast()
	// One button per rule, and the engine's preview is its confirmation
	// (Curro, 2026-10-03: an eye icon in the row filled a panel above
	// the table, and only then did a ▶ appear there — two controls
	// whose meaning depended on a hidden selection). Ask, then show the
	// count and the skip reasons in the dialog the operator has to
	// accept anyway. Cancelling writes nothing, so the preview needs no
	// button of its own.
	const [asking, setAsking] = useState(null)     // rule id being previewed
	const onRunNow = async (rule) => {
		if (!rule || asking) return
		setAsking(rule.id)
		try {
			const { raw, error: failed } = await preview.ask(rule.id)
			if (failed) { showToast.error(failed); return }
			const fmt = formatPreview(raw)
			if (fmt.refused) { showToast.error(`The sweep cannot run: ${fmt.refused}`); return }
			if (fmt.total === 0) {
				showToast.info(`"${rule.name}" would launch nothing right now.`)
				return
			}
			// The cost is the reason this asks at all, so it gets its own
			// line instead of being buried in the question.
			if (!await confirm({
				title: `Run "${rule.name}" now?`,
				body: 'The rule runs immediately, outside its schedule.',
				warning: `${fmt.total} search${fmt.total === 1 ? '' : 'es'} will be created, and searches are billed.`,
				detail: <PreviewDetail fmt={fmt}/>,
				confirmLabel: `Run now · ${fmt.total} searches`,
			})) return
			const report = await runNow(rule.id, 'run now from the Schedule screen')
			const created = (report.rules || []).reduce((s, r) => s + (r.created || 0), 0)
			setError(null)
			showToast.success(`Launched: ${created} searches created.`)
		} catch (_) { /* toast already shown by the hook */
		} finally { setAsking(null) }
	}
	const [editing, setEditing] = useState(null)   // rule being edited, {} to add
	const [error, setError] = useState(null)

	const sortedRules = [...rules].sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))
	// Persisted per rule, not as a whole list: the server validates each
	// one and its answer is what lands on screen (brief criterio 20).
	const commit = async (rule) => {
		try {
			await save(rule)
			setError(null)
			return true
		} catch (e) {
			setError(describeError(e))
			return false
		}
	}
	const upsert = async (rule) => {
		// The drawer stays open when the server refuses: the operator keeps
		// what they typed and reads why, instead of a drawer that closes as
		// if it had saved (brief criterio 20).
		if (await commit(rule)) setEditing(null)
	}
	const onDelete = async (rule) => {
		if (!await confirm({
			title: `Delete rule "${rule.name}"?`,
			body: 'The engine stops running on this schedule. Rules already launched are not affected.',
			confirmLabel: 'Delete rule',
			destructive: true,
		})) return
		remove(rule.id)
	}
	const duplicate = (rule) => {
		const { id, created_at, updated_at, ...rest } = rule
		commit({ ...rest, name: rule.name + ' (copy)' })
	}

	return (
		<Box>
			{confirmDialog}
			{/* While the drawer is open the message is shown inside it. */}
			{/* The card's head as the app's tables: icon title, the line about it, the create button */}
			<Box display="flex" alignItems="flex-start" gap={2} sx={{ mb: 2 }}>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					{/* What the card is, behind the ⓘ of its title */}
					<AppCardTitle icon="clock" title="Schedule rules">
						<AppInfoButton title="Schedule rules" sx={{ ml: 0.25 }}>
						Rules that decide how often the repricing engine runs for each
						pickup window. Overlaps are allowed — priority arbitrates
						(lower number wins).
						</AppInfoButton>
					</AppCardTitle>
				</Box>
				<AppToolbarCreateButton divider={false} label="New rule" icon="plus"
				                        onClick={() => setEditing({})} sx={{ textTransform: 'none' }}/>
			</Box>
			{error && editing === null && (
				<AppNote severity="error" sx={{ mb: 1.5 }}>{error}</AppNote>
			)}
			<SweepPreview preview={preview} rules={rules}/>

			{loading ? <AppTableSkeleton rows={6} columns={8}/> : (
				<Table size="small" sx={{
					'& tbody tr:nth-of-type(odd)':  { bgcolor: dsColorNew.white },
					'& tbody tr:nth-of-type(even)': { bgcolor: dsColorNew.tableRowAlt },
					'& tbody tr:hover':             { bgcolor: dsColorNew.hoverBgLight },
					'& td':                         { fontSize: 13, py: 1.1 },
				}}>
					<TableHead>
						<TableRow>
							<TableCell align="center" sx={{ width: 60 }}>Prio</TableCell>
							<TableCell>Name</TableCell>
							<TableCell>Pickup range</TableCell>
							<TableCell>Frequency</TableCell>
							<TableCell>Window</TableCell>
							<TableCell>Days</TableCell>
							<TableCell>Scope</TableCell>
							<TableCell>Status</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{sortedRules.length === 0 && (
							<TableRow>
								<TableCell colSpan={9} sx={{ py: 2, textAlign: 'center' }}>
									<AppEmptyLine>No schedule rules yet. Create the first one with "New rule".</AppEmptyLine>
								</TableCell>
							</TableRow>
						)}
						{sortedRules.map(r => {
							const runs = r.is_active ? ruleHoursForDay(r).length : 0
							return (
								<TableRow key={r.id} hover
								          sx={{ opacity: r.is_active ? 1 : 0.65 }}>
									<TableCell align="center">
										{r.priority ?? '—'}
									</TableCell>
									<TableCell sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{r.name}</TableCell>
									<TableCell>
										{r.mode === 'rolling'
											? <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{`DBA ${r.dba_from}–${r.dba_to}`}</Box>
											: <>
												<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>Fixed</Typography>
												<Box component="span" sx={{ whiteSpace: 'nowrap' }}>{`${r.fixed_from} → ${r.fixed_to}`}</Box>
											</>}
									</TableCell>
									<TableCell>
										<Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
											<span>{freqStr(r)}</span>
											{runs > 0 && <AppValueChip tone="accent">{runs}× / day</AppValueChip>}
										</Box>
									</TableCell>
									<TableCell>
										{r.frequency_kind === 'every_n_hours'
											? <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{`${r.window_start}–${r.window_end}`}</Box>
											: <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>}
									</TableCell>
									<TableCell>
										{r.weekdays.map(w => WEEKDAY_LABELS[w]).join(' ')}
									</TableCell>
									<TableCell>
										{scopeChips(r.office_codes)}
									</TableCell>
									<TableCell>
										<StatusPill live={r.is_active}
										            onToggle={() => setActive(r.id, !r.is_active)}/>
									</TableCell>
									<TableCell align="right">
										<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
											{/* Run now with the row's other actions: it asks first, with the number of searches */}
											<AppButtonIcon icon={asking === r.id ? 'spinner' : 'play'}
											               tooltip={asking === r.id ? 'Asking…' : 'Run now'}
											               disabled={asking !== null}
											               onClick={() => onRunNow(r)}/>
											<AppButtonIcon icon="pen" tooltip="Edit this rule" onClick={() => setEditing(r)}/>
											<AppButtonIcon icon="clone" onClick={() => duplicate(r)}/>
											<AppButtonIcon icon="trash-can" onClick={() => onDelete(r)}/>
										</Box>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			)}

			<Drawer open={editing !== null}
			        rule={editing}
			        allRules={rules}
			        onClose={() => setEditing(null)}
			        onSave={upsert}
			        onError={setError}
			        error={error}/>
		</Box>
	)
}

/** The engine's answer, as the body of the confirmation: what this run
 *  is about to create, and what it will leave out. */
function PreviewDetail({ fmt }) {
	return (
		<Box>
			{fmt.rules.map(r => (
				<Box key={r.ruleId} sx={{ mb: r.skipped.length ? 0.5 : 0 }}>
					<Box component="span" sx={{ fontWeight: 700 }}>
						{r.searches} search{r.searches === 1 ? '' : 'es'}
					</Box>
					{' '}over {r.pickupDates} pickup date{r.pickupDates === 1 ? '' : 's'}
					{' '}and {r.offices} office{r.offices === 1 ? '' : 's'}.
					{r.skipped.length > 0 && (
						<Box component="ul" sx={{ m: 0.5, pl: 2.5 }}>
							{r.skipped.map(s => <li key={s.label}>{s.count} × {s.label}</li>)}
						</Box>
					)}
				</Box>
			))}
		</Box>
	)
}

function freqStr(r) {
	if (r.frequency_kind === 'every_n_hours')  return `Every ${r.frequency_value} h`
	if (r.frequency_kind === 'specific_hours') return `Hours: ${(r.frequency_value || []).join(', ')}`
	return '—'
}

/** Inline pause switch: the app's Off/On switch (as Strategies and the AutoEurope tool), with the word
 *  beside it. Toggling it is audited server-side as its own action rather than as an edit. */
function StatusPill({ live, onToggle }) {
	return (
		<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
		     title={onToggle ? (live ? 'Pause this rule' : 'Resume this rule') : undefined}>
			<AppAntSwitch checked={live} onChange={() => onToggle?.()} disabled={!onToggle}/>
			<Box component="span" sx={tonePillSx(live ? 'ok' : 'neutral')}>
				{live ? 'Live' : 'Paused'}
			</Box>
		</Box>
	)
}

function scopeChips(offices) {
	const items = offices?.length ? offices.map(o => `${o}`) : ['All offices']
	return (
		<Box display="flex" gap={0.5} flexWrap="wrap">
			{items.slice(0, 5).map((t, i) => (
				<AppValueChip key={i}>{t}</AppValueChip>
			))}
			{items.length > 5 && <AppValueChip>{`+${items.length - 5}`}</AppValueChip>}
		</Box>
	)
}
